/**
 * One row of a searchable picker: the option's name with the typed text marked
 * in it, and its description underneath when it has one.
 *
 * A picker that searches a description has to show the description, or a match
 * arrives with nothing in the name to explain why it is there. Marking the
 * matched text is the other half of the same promise — it is what tells the
 * reader which of several near-identical names is the one they typed toward.
 * Both belong to any picker that searches more than a label, so neither lives
 * with a particular one.
 */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import Highlighter from 'react-highlight-words';

/**
 * @param {object} props
 * @param {string} props.label - the option's name.
 * @param {?string} [props.description] - its supporting line; omitted when empty.
 * @param {?string} [props.searchText] - the reader's typed text, marked in both.
 * @param {string} [props.primaryVariant] - Typography variant for the name.
 * @param {string} [props.secondaryVariant] - Typography variant for the description.
 */
export default function HighlightedOptionRow({
  label,
  description = null,
  searchText = '',
  primaryVariant = 'body1',
  secondaryVariant = 'body2',
}) {
  const searchWords = [(searchText ?? '').trim()];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
      <Typography component="div" variant={primaryVariant} sx={{ width: '100%' }}>
        <Highlighter searchWords={searchWords} autoEscape textToHighlight={label ?? ''} />
      </Typography>
      {description ? (
        <Typography component="div" variant={secondaryVariant} color="textSecondary" sx={{ width: '100%', mt: '4px' }}>
          <Highlighter searchWords={searchWords} autoEscape textToHighlight={description} />
        </Typography>
      ) : null}
    </Box>
  );
}
